import React, { useCallback, useEffect, useState } from 'react';
import {
  LuCalendar,
  LuCheck,
  LuChevronDown,
  LuChevronUp,
  LuClock,
  LuRefreshCw,
  LuTrash2,
  LuUserCheck,
  LuUserPlus,
  LuX,
} from 'react-icons/lu';
import { subscribeRealtimeInvalidate } from '../../realtime/realtimeBus';

function ProgressBar({ value, label }) {
  return (
    <div className="teams-progress" aria-label={label}>
      <div className="teams-progress-track">
        <div className="teams-progress-fill" style={{ width: `${Math.min(100, value)}%` }} />
      </div>
      <span className="teams-sub">{label}</span>
    </div>
  );
}

function formatRefreshed(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return null;
  }
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return '—';
  }
}

function participationLabel(m) {
  if (m.participationStatus === 'AWAITING_CREATOR') return 'Awaiting creator approval';
  if (m.participationStatus === 'PENDING') return 'Pending accept';
  if (m.participationStatus === 'CREATOR_REJECTED') return 'Invite rejected by creator';
  if (m.status === 'COMPLETED') return 'Completed';
  return m.status || '—';
}

export default function TeamAssignmentCard({
  orgId,
  apiClient,
  summary,
  expanded,
  onToggle,
  onDelete,
}) {
  const [deleting, setDeleting] = useState(false);
  const [detail, setDetail] = useState(summary);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [orgMembers, setOrgMembers] = useState([]);
  const [inviteUserId, setInviteUserId] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [reviewingId, setReviewingId] = useState(null);
  const failStreakRef = React.useRef(0);

  const fetchDetail = useCallback(async (withSync = false) => {
    if (!expanded || !summary?.id) return;
    if (failStreakRef.current >= 3) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    setLoading(true);
    try {
      const syncQ = withSync ? '?sync=true' : '';
      const res = await apiClient.get(`/api/organizations/${orgId}/assignments/${summary.id}${syncQ}`);
      setDetail(res.data);
      setFetchError('');
      failStreakRef.current = 0;
    } catch (err) {
      failStreakRef.current += 1;
      const msg = err.response?.data?.detail
        || err.response?.data?.message
        || 'Could not refresh assignment details.';
      setFetchError(msg);
      setDetail((prev) => ({ ...(prev || summary), ...(summary || {}) }));
    } finally {
      setLoading(false);
    }
  }, [apiClient, orgId, summary, expanded]);

  useEffect(() => {
    setDetail((prev) => {
      if (!summary?.id) return summary;
      if (prev?.id !== summary.id) return summary;
      if (!expanded) return summary;
      const { memberProgress: _mp, refreshedAt: _ra, ...headerFields } = summary;
      return { ...prev, ...headerFields };
    });
  }, [summary, expanded]);

  useEffect(() => {
    if (!expanded) {
      failStreakRef.current = 0;
      setFetchError('');
      return undefined;
    }
    fetchDetail(true);
    const interval = window.setInterval(() => fetchDetail(false), 20000);
    const unsub = subscribeRealtimeInvalidate((d) => {
      if (d?.reason === 'read-toggle' || d?.reason === 'team-assignment-progress') {
        fetchDetail(false);
      }
    });
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchDetail(false);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      unsub();
    };
  }, [expanded, fetchDetail]);

  useEffect(() => {
    if (!expanded || !detail?.canInviteMembers) return undefined;
    let cancelled = false;
    apiClient.get(`/api/organizations/${orgId}/members`)
      .then((res) => {
        if (!cancelled) setOrgMembers(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setOrgMembers([]);
      });
    return () => { cancelled = true; };
  }, [apiClient, orgId, expanded, detail?.canInviteMembers]);

  const a = expanded ? (detail || summary) : summary;
  const target = a.targetCount ?? 10;
  const myShown = a.myDisplayCount ?? Math.min(a.myProgressCount ?? 0, target);
  const myPct = a.myPercent ?? 0;

  const sync = async (e) => {
    e.stopPropagation();
    setSyncing(true);
    try {
      const res = await apiClient.post(`/api/organizations/${orgId}/assignments/${a.id}/sync`);
      setDetail(res.data);
    } finally {
      setSyncing(false);
    }
  };

  const remove = async (e) => {
    e.stopPropagation();
    if (!onDelete || deleting) return;
    const label = a.title || 'this assignment';
    if (!window.confirm(`Delete "${label}"? Progress and invites will be removed.`)) return;
    setDeleting(true);
    try {
      await onDelete(a.id);
    } finally {
      setDeleting(false);
    }
  };

  const showInviteStats = a.canDelete || a.isCreatedByMe
    || a.invitesPending != null || a.invitesAccepted != null;

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!inviteUserId || inviting) return;
    setInviteError('');
    setInviting(true);
    try {
      await apiClient.post(`/api/organizations/${orgId}/assignments/${a.id}/invites`, {
        userId: Number(inviteUserId),
      });
      setInviteUserId('');
      await fetchDetail(false);
    } catch (err) {
      setInviteError(err.response?.data?.message || err.response?.data?.detail || 'Could not send invite.');
    } finally {
      setInviting(false);
    }
  };

  const reviewInvite = async (requestId, action) => {
    setReviewingId(requestId);
    try {
      await apiClient.patch(`/api/organizations/${orgId}/assignment-invites/${requestId}`, { action });
      await fetchDetail(false);
    } finally {
      setReviewingId(null);
    }
  };

  const existingAssigneeIds = new Set((a.memberProgress || []).map((m) => m.userId));
  const inviteCandidates = orgMembers.filter((m) => !existingAssigneeIds.has(m.userId));

  return (
    <article className={`teams-assignment-panel ${expanded ? 'is-open' : ''}`}>
      <button type="button" className="teams-assignment-trigger" onClick={onToggle} aria-expanded={expanded}>
        <div className="teams-assignment-trigger-main">
          <strong>{a.title}</strong>
          <span className="teams-badge">{a.status}</span>
        </div>
        <p className="teams-sub teams-assignment-trigger-meta">
          {a.assigneeType}
          {a.notebookTopic ? ` · ${a.notebookTopic}` : ' · any topic'}
          {' · '}
          {a.membersCompleted ?? 0}/{a.assigneeCount ?? '—'} done
          {a.teamPercent != null && ` · team ${a.teamPercent}%`}
          {showInviteStats && (
            <>
              {' · '}
              <LuUserCheck aria-hidden className="teams-inline-icon" />
              {a.invitesAccepted ?? 0} accepted
              {' · '}
              <LuClock aria-hidden className="teams-inline-icon" />
              {a.invitesPending ?? 0} pending
              {(a.invitesAwaitingCreator ?? 0) > 0 && (
                <>
                  {' · '}
                  {a.invitesAwaitingCreator} need your approval
                </>
              )}
            </>
          )}
          {a.createdAt && (
            <>
              {' · '}
              <LuCalendar aria-hidden className="teams-inline-icon" />
              {formatDateTime(a.createdAt)}
            </>
          )}
        </p>
        <span className="teams-assignment-chevron" aria-hidden>
          {expanded ? <LuChevronUp /> : <LuChevronDown />}
        </span>
      </button>

      {expanded && (
        <div className="teams-assignment-body">
          <div className="teams-assignment-live">
            <span className={`teams-live-dot ${loading && !fetchError ? 'is-pulse' : ''}`} aria-hidden />
            {fetchError ? 'Using cached summary' : 'Live updates'}
            {a.refreshedAt && !fetchError && (
              <span className="teams-sub"> · Last sync {formatRefreshed(a.refreshedAt)}</span>
            )}
          </div>
          {fetchError && (
            <p className="teams-error teams-assignment-fetch-error" role="status">
              {fetchError}
              {' '}
              Restart the API server if this persists after a deploy.
            </p>
          )}

          <dl className="teams-assignment-meta">
            <div>
              <dt>Created</dt>
              <dd>{formatDateTime(a.createdAt)}</dd>
            </div>
            <div>
              <dt>Creator</dt>
              <dd>{a.createdByName || `User ${a.createdBy}`}</dd>
            </div>
            <div>
              <dt>Last updated</dt>
              <dd>{formatDateTime(a.updatedAt)}</dd>
            </div>
            <div>
              <dt>Due</dt>
              <dd>{a.dueAt ? formatDateTime(a.dueAt) : 'No due date'}</dd>
            </div>
            {a.appliesToMe && (
              <>
                <div>
                  <dt>You accepted</dt>
                  <dd>{formatDateTime(a.myAcceptedAt)}</dd>
                </div>
                <div>
                  <dt>You completed</dt>
                  <dd>{formatDateTime(a.myCompletedAt)}</dd>
                </div>
              </>
            )}
          </dl>

          {(a.pendingInviteApprovals || []).length > 0 && (
            <div className="teams-assignment-approvals">
              <h3 className="teams-assignment-subhead">
                <LuUserCheck aria-hidden />
                Member invites awaiting your approval
              </h3>
              <ul className="teams-approval-list">
                {a.pendingInviteApprovals.map((row) => (
                  <li key={row.id} className="teams-approval-item">
                    <div>
                      <strong>{row.targetUserName}</strong>
                      <p className="teams-sub">
                        Proposed by {row.invitedByName} · {formatDateTime(row.createdAt)}
                      </p>
                    </div>
                    <div className="teams-request-actions">
                      <button
                        type="button"
                        className="teams-btn teams-btn--secondary"
                        disabled={reviewingId === row.id}
                        onClick={() => reviewInvite(row.id, 'REJECT')}
                      >
                        <LuX aria-hidden />
                        Reject
                      </button>
                      <button
                        type="button"
                        className="teams-btn"
                        disabled={reviewingId === row.id}
                        onClick={() => reviewInvite(row.id, 'APPROVE')}
                      >
                        <LuCheck aria-hidden />
                        Approve
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {a.canInviteMembers && (
            <form className="teams-assignment-invite" onSubmit={sendInvite}>
              <h3 className="teams-assignment-subhead">
                <LuUserPlus aria-hidden />
                Invite a teammate
              </h3>
              <p className="teams-sub">
                {a.isCreatedByMe
                  ? 'Invites you send go out immediately.'
                  : 'Invites you send need approval from the assignment creator before they appear in Requests.'}
              </p>
              <div className="teams-assignment-invite-row">
                <select
                  value={inviteUserId}
                  onChange={(e) => setInviteUserId(e.target.value)}
                  aria-label="Team member to invite"
                >
                  <option value="">Select member…</option>
                  {inviteCandidates.map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.name || m.email}
                    </option>
                  ))}
                </select>
                <button type="submit" className="teams-btn" disabled={!inviteUserId || inviting}>
                  {inviting ? 'Sending…' : 'Send invite'}
                </button>
              </div>
              {inviteError && <p className="teams-error" role="alert">{inviteError}</p>}
            </form>
          )}

          {a.appliesToMe && (
            <ProgressBar
              value={myPct}
              label={`Your progress: ${myShown} / ${target} (${myPct}%) · ${a.myStatus}`}
            />
          )}

          <div className="teams-assignment-team-bar">
            <span className="teams-sub">Team average</span>
            <ProgressBar value={a.teamPercent ?? 0} label={`${a.teamPercent ?? 0}% across assignees`} />
          </div>

          <div className="teams-table-wrap">
            <table className="teams-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Progress</th>
                  <th>Status</th>
                  <th>Accepted</th>
                  <th>Started</th>
                  <th>Completed</th>
                  <th>Last activity</th>
                </tr>
              </thead>
              <tbody>
                {(a.memberProgress || []).length === 0 ? (
                  <tr>
                    <td colSpan={7} className="teams-sub">No assignees yet — add group members or invite team.</td>
                  </tr>
                ) : (
                  a.memberProgress.map((m) => {
                    const shown = m.displayCount ?? Math.min(m.progressCount ?? 0, m.targetCount ?? target);
                    const pct = m.percent ?? 0;
                    return (
                      <tr key={m.userId}>
                        <td>{m.name || m.email || `User ${m.userId}`}</td>
                        <td>
                          <div className="teams-progress teams-progress--inline">
                            <div className="teams-progress-track">
                              <div className="teams-progress-fill" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="teams-sub">{shown} / {m.targetCount ?? target} ({pct}%)</span>
                          </div>
                        </td>
                        <td>{participationLabel(m)}</td>
                        <td className="teams-sub teams-nowrap">{formatDateTime(m.acceptedAt)}</td>
                        <td className="teams-sub teams-nowrap">{formatDateTime(m.startedAt)}</td>
                        <td className="teams-sub teams-nowrap">{formatDateTime(m.completedAt)}</td>
                        <td className="teams-sub teams-nowrap">{formatDateTime(m.lastProgressAt)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="teams-assignment-actions">
            <button
              type="button"
              className="teams-btn teams-btn--secondary teams-assignment-sync"
              onClick={sync}
              disabled={syncing}
            >
              <LuRefreshCw aria-hidden className={syncing ? 'teams-spin' : undefined} />
              {syncing ? 'Syncing…' : 'Sync from read history'}
            </button>
            {a.canDelete && onDelete && (
              <button
                type="button"
                className="teams-btn teams-btn--danger"
                onClick={remove}
                disabled={deleting}
              >
                <LuTrash2 aria-hidden />
                {deleting ? 'Deleting…' : 'Delete assignment'}
              </button>
            )}
          </div>
        </div>
      )}
    </article>
  );
}
